/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-cm-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import i18n from 'i18next';
import moment from 'moment';
import {
    STUDY_AUTOMATION_COLOR_COLUMN_TYPES,
    CONTROLS_AUTOMATION_COLOR_COLUMN_TYPES,
} from '@components/admin/studies/study-settings-dialogs/automation-color.data';
import { useBoolean, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_SITES } from 'queries/sites';
import { GET_STUDY, SAVE_CENTRAL_MONITORING_CONFIGURATION } from 'queries/studies';
import { ControlsAutomationColor, DashboardType, FieldTag, StudyAutomationColor } from 'types/graphql-global-types';
import { formatDateUTC_DEPRECATED, stringDateWithoutTimeFromUtc } from 'utils/convertor';
import NotificationService from 'utils/notification';
import { validateFields } from 'utils/validation';
import { FieldSelector } from 'views/components/central-monitoring/field-selector';
import { SaveButton } from 'views/components/core/buttons';
import DatePicker from 'views/components/core/date-picker';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import Loading from 'views/components/core/loading';
import CustomMultiSelect from 'views/components/core/multi-select/multi-select';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

import 'views/components/admin/studies/study-settings-dialogs/study-settings-dialogs.scss';

const minNumberThreshold = 1;
const maxNumberThreshold = 100;

const minAdjustmentWeight = 0.01;
const maxAdjustmentWeight = 2;

const transformCMStudyData = (study: StudyFields) => ({
    importDefinitionsKey: study?.importDefinitionsKey ?? '',
    volumeAdjustedScoring: study.volumeAdjustedScoring || false,
    siteStatusesToExclude: study.sitesStatusesToExclude?.map((siteName: string) => ({
        name: siteName,
        id: siteName,
    })),
    scoreGreenThreshold: study.scoreGreenThreshold || 35,
    scoreAmberThreshold: study.scoreAmberThreshold || 70,
    volumeAdjustedScoringNumberField: study.volumeAdjustedScoringNumberField,
    scoreThreshold: +study.scoreThreshold,
    adjustmentWeight: study.adjustmentWeight,
    svtDependency: study.svtDependency,
    calculateAssessments: study.calculateAssessments,
    calculateCrfs: study.calculateCrfs,
    calculateVisits: study.calculateVisits,
    lastSubjectScreenedDate: stringDateWithoutTimeFromUtc(study.lastSubjectScreenedDate),
    lastSubjectEnrolledDate: stringDateWithoutTimeFromUtc(study.lastSubjectEnrolledDate),
    lastSubjectRandomisedDate: stringDateWithoutTimeFromUtc(study.lastSubjectRandomisedDate),
    assessmentAssociation: study.assessmentAssociation,
    useInformedConsentedDate: study.useInformedConsentedDate,
    automationActive: study.automationActive,
    studyAutomationColor: study.automationActive ? study.studyAutomationColor : StudyAutomationColor.NONE,
    controlsAutomationActive: study.controlsAutomationActive,
    controlsAutomationColor: study.controlsAutomationActive
        ? study.controlsAutomationColor
        : ControlsAutomationColor.NONE,
    integrationActive: study.integrationActive,
    integrationStudyNumber: study.integrationActive ? study.integrationStudyNumber : '',
});

const EditCmDialog = ({
    study,
    open,
    onClose,
    canEdit,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    canEdit: boolean;
}) => {
    const { t } = useTranslation();
    const { t: tAdmin } = useTranslation('admin');
    const [saveStudy] = useMutation<{ saveCentralMonitoringConfiguration: string | null }>(
        SAVE_CENTRAL_MONITORING_CONFIGURATION,
    );
    const disabledSaveButton = useBoolean(true);
    const initialState = transformCMStudyData(study);
    const initialErrors = {
        scoreThreshold: '',
        adjustmentWeight: '',
        scoreGreenThreshold: '',
        scoreAmberThreshold: '',
        volumeAdjustedScoringNumberField: '',
        importDefinitionsKey: '',
        studyAutomationColor: '',
        controlsAutomationColor: '',
        integrationStudyNumber: '',
    };
    const [state, setState] = useState({ ...initialState });
    const [errors, setErrors] = useState({ ...initialErrors });
    const [loadSitesData, { loading: loadingSites, data: dataSites }] = useLazyQuery(GET_SITES);

    const getThresholdOptions = (field: string) => () => {
        const options: { id: number | null; text: string; active: boolean }[] = [];
        for (let index = minNumberThreshold; index <= maxNumberThreshold; index++) {
            options.push({ id: index, text: String(index), active: true });
        }
        options.unshift({ id: null, text: t(`admin:${field}Select`), active: true });
        return options;
    };

    const validate = (fieldName: string, value?: number | string, max?: number, min?: number) => {
        const number = +value;
        let error = null;
        if (fieldName === 'scoreThreshold')
            if (number > max || number < min)
                error = t('admin:enterValueBetween', { min: minNumberThreshold, max: maxNumberThreshold });
        if (fieldName === 'adjustmentWeight')
            if (number > max || number < min)
                error = t('admin:enterValueBetween', { min: minAdjustmentWeight, max: maxAdjustmentWeight });
        if (fieldName === 'importDefinitionsKey') {
            error = validateFields(value as string, fieldName, 50, false);
            setErrors({
                ...errors,
                ...error,
            });
            return;
        }
        setErrors({ ...errors, [fieldName]: error });
        return {
            [fieldName]: error,
        };
    };

    const checkRequiredFields = (fields: string[]) => {
        let isValid = true;
        let newErrors = { ...errors };
        fields.forEach((field) => {
            const value = (state as any)[field];
            let error;
            if (typeof value === 'number') {
                const min = field === 'scoreThreshold' ? minNumberThreshold : minAdjustmentWeight;
                const max = field === 'scoreThreshold' ? maxNumberThreshold : maxAdjustmentWeight;
                const errorResult = validateFields(String(value), field, 0, true);
                error = errorResult[field] ? errorResult : validate(field, +value, max, min);
            } else if (field === 'studyAutomationColor' && state.studyAutomationColor === StudyAutomationColor.NONE) {
                error = { [field as string]: `${i18n.t('ra:requiredFieldError')}` };
            } else if (
                field === 'controlsAutomationColor' &&
                state.controlsAutomationColor === ControlsAutomationColor.NONE
            ) {
                error = { [field as string]: `${i18n.t('ra:requiredFieldError')}` };
            } else if (field === 'integrationStudyNumber' && state.integrationStudyNumber === '') {
                error = { [field as string]: `${i18n.t('ra:requiredFieldError')}` };
            } else {
                error = validateFields(value, field, 0, true);
            }
            newErrors = {
                ...newErrors,
                ...error,
            };
        });

        if (Object.keys(newErrors).filter((item) => (newErrors as any)[item]).length > 0) {
            isValid = false;
        }
        setErrors({ ...newErrors });
        return isValid;
    };

    useEffect(() => {
        if (study && open) setState(transformCMStudyData(study));
    }, [study, open]);

    useEffect(() => {
        if (study && open && !dataSites?.sites) loadSitesData({ variables: { studyId: study.id } });
    }, [study, open]);

    useEffect(() => {
        disabledSaveButton.setValue(JSON.stringify(initialState) === JSON.stringify(state));
    }, [state]);

    const close = () => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        setErrors({ ...initialErrors });
        onClose();
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
        onClose();
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const checkForErrors = () => !!Object.keys(errors).filter((item) => (errors as any)[item]).length;

    const onUpdate = () => {
        const requiredFields = ['scoreGreenThreshold', 'scoreAmberThreshold'];

        if (state.volumeAdjustedScoring) {
            requiredFields.push('volumeAdjustedScoringNumberField', 'adjustmentWeight', 'scoreThreshold');
        }

        if (state.automationActive) requiredFields.push('studyAutomationColor');

        if (state.controlsAutomationActive) requiredFields.push('controlsAutomationColor');

        if (state.integrationActive) requiredFields.push('integrationStudyNumber');

        if (!checkForErrors() && checkRequiredFields(requiredFields)) {
            disabledSaveButton.setTrue();

            saveStudy({
                variables: {
                    studyId: study.id,
                    importDefinitionsKey: state.importDefinitionsKey,
                    volumeAdjustedScoring: state.volumeAdjustedScoring,
                    siteStatusesToExclude: state.siteStatusesToExclude.map((item: { name: string }) => item.name),
                    scoreGreenThreshold: state.scoreGreenThreshold,
                    scoreAmberThreshold: state.scoreAmberThreshold,
                    scoreThreshold: state.scoreThreshold,
                    volumeAdjustedScoringNumberField: state.volumeAdjustedScoringNumberField,
                    adjustmentWeight: state.adjustmentWeight,
                    svtDependency: state.svtDependency,
                    calculateAssessments: state.calculateAssessments,
                    calculateCrfs: state.calculateCrfs,
                    calculateVisits: state.calculateVisits,
                    lastSubjectScreenedDate: formatDateUTC_DEPRECATED(state.lastSubjectScreenedDate, false),
                    lastSubjectEnrolledDate: formatDateUTC_DEPRECATED(state.lastSubjectEnrolledDate, false),
                    lastSubjectRandomisedDate: formatDateUTC_DEPRECATED(state.lastSubjectRandomisedDate, false),
                    assessmentAssociation: state.assessmentAssociation,
                    useInformedConsentedDate: state.useInformedConsentedDate,
                    automationActive: state.automationActive,
                    studyAutomationColor: state.studyAutomationColor,
                    controlsAutomationActive: state.controlsAutomationActive,
                    controlsAutomationColor: state.controlsAutomationColor,
                    integrationActive: state.integrationActive,
                    integrationStudyNumber: state.integrationStudyNumber,
                },
                refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
            })
                .then((response) => {
                    // If there is string content, it means error
                    if (response.data?.saveCentralMonitoringConfiguration) errorRequest();
                    else successRequest();
                })
                .catch(() => {
                    errorRequest();
                });
        }
    };

    const onChange = (fieldName: string) => (event: any) => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        if (fieldName === 'adjustmentWeight' || fieldName === 'scoreThreshold') {
            setState({ ...state, [fieldName]: parseFloat(Number(event.target.value).toFixed(2)) });
        } else {
            setState({ ...state, [fieldName]: event.target.value });
        }
        const min = fieldName === 'scoreThreshold' ? minNumberThreshold : minAdjustmentWeight;
        const max = fieldName === 'scoreThreshold' ? maxNumberThreshold : maxAdjustmentWeight;
        validate(fieldName, event.target.value, max, min);
    };

    const onGreenThresholdChange = () => (event: any) => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        const isEmberLessThenGreen =
            +state.scoreAmberThreshold < +event.target.value && state.scoreAmberThreshold !== null;
        setErrors({
            ...errors,
            scoreAmberThreshold: '',
            scoreGreenThreshold: isEmberLessThenGreen ? t('admin:amberThresholdLessThenGreen') : '',
        });
        setState({ ...state, scoreGreenThreshold: event.target.value });
    };

    const onAmberThresholdChange = () => (event: any) => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        const isEmberLessThenGreen = +state.scoreGreenThreshold > +event.target.value;
        setErrors({
            ...errors,
            scoreAmberThreshold: isEmberLessThenGreen ? t('admin:amberThresholdLessThenGreen') : '',
            scoreGreenThreshold: '',
        });
        setState({ ...state, scoreAmberThreshold: event.target.value });
    };

    const onAutomationActive = () => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        setState({
            ...state,
            automationActive: !state.automationActive,
            studyAutomationColor: StudyAutomationColor.NONE,
        });

        setErrors({
            ...errors,
            studyAutomationColor: '',
        });
    };

    const onControlsAutomationActive = () => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        setState({
            ...state,
            controlsAutomationActive: !state.controlsAutomationActive,
            controlsAutomationColor: ControlsAutomationColor.NONE,
        });

        setErrors({
            ...errors,
            controlsAutomationColor: '',
        });
    };

    const onIntegrationActive = () => {
        if (disabledSaveButton.value) {
            disabledSaveButton.setFalse();
        }
        setState({
            ...state,
            integrationActive: !state.integrationActive,
            integrationStudyNumber: '',
        });

        setErrors({
            ...errors,
            integrationStudyNumber: '',
        });
    };

    const onChangeVolumeAdjustedScoring = () => {
        disabledSaveButton.setFalse();

        setState({
            ...state,
            volumeAdjustedScoring: !state.volumeAdjustedScoring,
            volumeAdjustedScoringNumberField: '',
            scoreThreshold: 0,
            adjustmentWeight: 0,
        });

        setErrors({
            ...errors,
            volumeAdjustedScoringNumberField: '',
            scoreThreshold: '',
            adjustmentWeight: '',
        });
    };

    const onDateChange = (name: string) => (event: any) => {
        disabledSaveButton.setFalse();

        setState({
            ...state,
            [name]: event?.value ? moment(event.value) : null,
        });
    };

    const greenThresholdOptions = useMemo(getThresholdOptions('greenThreshold'), []);
    const amberThresholdOptions = useMemo(getThresholdOptions('amberThreshold'), []);

    if (loadingSites) {
        return <Loading float />;
    }

    return (
        <CustomDialog className="edit-cm-dialog" onClose={close} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="edit-cm-title">
                {t('admin:editCMSetup')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="edit-cm-dialog-content">
                <div className="edit-cm-date-wrapper selects-wrapper">
                    <FormControl variant="outlined" className="form-action-type mr">
                        <DatePicker
                            label={t('admin:lastSubjectScreenedDate')}
                            customId="edit-cm-screened-date"
                            selectedDate={state.lastSubjectScreenedDate}
                            handleDateChange={onDateChange('lastSubjectScreenedDate')}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-action-type">
                        <DatePicker
                            label={t('admin:lastSubjectEnrolledDate')}
                            customId="edit-cm-enrolled-date"
                            selectedDate={state.lastSubjectEnrolledDate}
                            handleDateChange={onDateChange('lastSubjectEnrolledDate')}
                        />
                    </FormControl>
                </div>
                <div className="edit-cm-date-wrapper selects-wrapper">
                    <FormControl variant="outlined" className="form-action-type not-full">
                        <DatePicker
                            label={t('admin:lastSubjectRandomizedDate')}
                            customId="edit-cm-randomized-date"
                            selectedDate={state.lastSubjectRandomisedDate}
                            handleDateChange={onDateChange('lastSubjectRandomisedDate')}
                        />
                    </FormControl>
                </div>

                <OutlinedWrapper label={t('admin:cmIntegration')}>
                    <div className="type-edc-wrapper no-mb">
                        <FormControl
                            variant="outlined"
                            className="form-control-type not-full mr !k-mb-7 !k-justify-content-center"
                        >
                            <Checkbox
                                rounded={null}
                                checked={state.integrationActive}
                                disabled={!!study.hasIntegrationActions}
                                onChange={onIntegrationActive}
                                label={`${t('admin:cmIntegrationActive')}`}
                                data-testid="cm-integration-active-checkbox"
                                size="medium"
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type ">
                            <TextField
                                label={`${t('admin:externalSystemStudyID')}${state.integrationActive ? '*' : ''}`}
                                id="external-system-study-id"
                                variant="outlined"
                                color="secondary"
                                value={state.integrationStudyNumber}
                                onChange={onChange('integrationStudyNumber')}
                                error={!!errors.integrationStudyNumber}
                                disabled={!!study.hasIntegrationActions || !state.integrationActive}
                                helperText={
                                    <div style={{ color: '#ae0015' }} className="remaining-text ">
                                        {errors.integrationStudyNumber}
                                    </div>
                                }
                            />
                        </FormControl>
                    </div>
                </OutlinedWrapper>

                <OutlinedWrapper label={t('admin:siteObsAndReviewLogsAutomation')}>
                    <div className="type-edc-wrapper">
                        <FormControl
                            variant="outlined"
                            className="form-control-type not-full mr k-justify-content-center"
                        >
                            <Checkbox
                                rounded={null}
                                checked={state.automationActive}
                                onChange={onAutomationActive}
                                label={`${t('admin:siteAutomationActive')}`}
                                data-testid="automation-active-checkbox"
                                size="medium"
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type">
                            <SelectWithCategories
                                customClassName="edit-cm-studyAutomationColor"
                                showWarning={false}
                                fieldSelect="studyAutomationColor"
                                onChange={(fieldName: string) => (event: any) => {
                                    onChange(fieldName)(event);
                                }}
                                label={`${t('admin:siteAutomationTrigger')}${state.automationActive ? '*' : ''}`}
                                showLabels={false}
                                dataArray={STUDY_AUTOMATION_COLOR_COLUMN_TYPES}
                                customId="edit-cm-studyAutomationColor"
                                currentState={state.studyAutomationColor}
                                disabled={!state.automationActive}
                                error={errors.studyAutomationColor}
                            />
                        </FormControl>
                    </div>
                </OutlinedWrapper>
                <OutlinedWrapper label={t('admin:controlsObsAndReviewLogsAutomation')}>
                    <div className="type-edc-wrapper">
                        <FormControl
                            variant="outlined"
                            className="form-control-type not-full mr k-justify-content-center"
                        >
                            <Checkbox
                                rounded={null}
                                checked={state.controlsAutomationActive}
                                onChange={onControlsAutomationActive}
                                label={`${t('admin:controlsAutomationActive')}`}
                                data-testid="controls-automation-active-checkbox"
                                size="medium"
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type">
                            <SelectWithCategories
                                customClassName="edit-cm-controlsAutomationColor"
                                showWarning={false}
                                fieldSelect="controlsAutomationColor"
                                onChange={(fieldName: string) => (event: any) => {
                                    onChange(fieldName)(event);
                                }}
                                label={`${t('admin:controlsAutomationTrigger')}${state.controlsAutomationActive ? '*' : ''}`}
                                showLabels={false}
                                dataArray={CONTROLS_AUTOMATION_COLOR_COLUMN_TYPES(tAdmin)}
                                customId="edit-cm-controlsAutomationColor"
                                currentState={state.controlsAutomationColor}
                                disabled={!state.controlsAutomationActive}
                                error={errors.controlsAutomationColor}
                            />
                        </FormControl>
                    </div>
                </OutlinedWrapper>

                <CustomMultiSelect
                    allowCustom
                    items={[]}
                    onChange={(value: any) => {
                        if (value.includes(undefined)) {
                            return;
                        }

                        disabledSaveButton.setFalse();
                        value = value || [];
                        const newValue = value.map((item: any) => {
                            if (Object.prototype.hasOwnProperty.call(item, 'id')) {
                                return item;
                            }
                            return {
                                id: item,
                                name: item,
                            };
                        });
                        setState({
                            ...state,
                            siteStatusesToExclude: [...newValue],
                        });
                    }}
                    value={state.siteStatusesToExclude}
                    label={t('admin:siteStatusToExclude')}
                    customClassName="form-action-type risk-categories edit-cm-multiselect"
                    customId="edit-cm-excludedSites"
                    placeholder={t('admin:textMatchToSourceDataRequired')}
                />
                <OutlinedWrapper label={t('admin:siteScore')}>
                    <div className="type-edc-wrapper">
                        <FormControl variant="outlined" className="form-control-type not-full mr">
                            <SelectWithCategories
                                customClassName="edit-cm-scoreGreenThreshold"
                                showWarning={false}
                                fieldSelect="scoreGreenThreshold"
                                onChange={onGreenThresholdChange}
                                label={`${t('admin:greenThreshold')}*`}
                                showLabels={false}
                                labelField="scoreGreenThreshold"
                                dataArray={greenThresholdOptions}
                                customId="edit-cm-scoreGreenThreshold"
                                currentState={state.scoreGreenThreshold}
                                error={errors.scoreGreenThreshold}
                            />
                        </FormControl>
                        <FormControl variant="outlined" className="form-control-type not-full">
                            <SelectWithCategories
                                customClassName="edit-cm-scoreAmberThreshold"
                                showWarning={false}
                                fieldSelect="scoreAmberThreshold"
                                onChange={onAmberThresholdChange}
                                label={`${t('admin:amberThreshold')}*`}
                                showLabels={false}
                                labelField="scoreAmberThreshold"
                                dataArray={amberThresholdOptions}
                                customId="edit-cm-scoreAmberThreshold"
                                currentState={state.scoreAmberThreshold}
                                error={errors.scoreAmberThreshold}
                            />
                        </FormControl>
                    </div>
                </OutlinedWrapper>
                <div className="value-list">
                    <Checkbox
                        rounded={null}
                        checked={state.volumeAdjustedScoring}
                        onChange={onChangeVolumeAdjustedScoring}
                        label={`${t('admin:volumeAdjustedScoring')}`}
                        data-testid="volumn-adjusted-scoring-checkbox"
                        size="medium"
                    />
                </div>
                {!!state.volumeAdjustedScoring && (
                    <OutlinedWrapper label={t('admin:volumeAdjustedScoring')}>
                        <div className="type-edc-wrapper">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <FieldSelector
                                    customClassName="edit-cm-subjectPopulation"
                                    fieldSelect="volumeAdjustedScoringNumberField"
                                    onChange={(value: any) =>
                                        onChange('volumeAdjustedScoringNumberField')({ target: { value } })
                                    }
                                    label={`${t('admin:subjectPopulation')}*`}
                                    labelField="subjectPopulation"
                                    customId="row-subjectPopulation"
                                    value={state.volumeAdjustedScoringNumberField}
                                    error={errors.volumeAdjustedScoringNumberField}
                                    dataSource={DashboardType.SITE}
                                    formats={[]}
                                    tags={[FieldTag.SUBJECT_POPULATION]}
                                />
                            </FormControl>
                        </div>
                        <div className="type-edc-wrapper no-mb">
                            <FormControl variant="outlined" className="form-control-type not-full mr">
                                <TextField
                                    label={`${t('admin:siteScoreThreshold')}*`}
                                    id="edit-cm-scoreThreshold"
                                    fullWidth
                                    type="number"
                                    value={state.scoreThreshold}
                                    onChange={onChange('scoreThreshold')}
                                    placeholder={t('admin:selectSiteScoreThreshold')}
                                    variant="outlined"
                                    color="secondary"
                                    helperText={
                                        <span
                                            style={errors.scoreThreshold ? { color: '#ae0015' } : {}}
                                            className="remaining-text"
                                        >
                                            {errors.scoreThreshold
                                                ? errors.scoreThreshold
                                                : t('admin:enterValueBetween', {
                                                      min: minNumberThreshold,
                                                      max: maxNumberThreshold,
                                                  })}
                                        </span>
                                    }
                                    className="before-select"
                                    error={!!errors.scoreThreshold}
                                />
                            </FormControl>
                            <FormControl variant="outlined" className="form-control-type not-full">
                                <TextField
                                    value={state.adjustmentWeight}
                                    error={errors.adjustmentWeight}
                                    onChange={onChange('adjustmentWeight')}
                                    id="edit-cm-adjustmentWeigh"
                                    type="number"
                                    fullWidth
                                    placeholder={t('admin:adjustmentWeightEnter')}
                                    variant="outlined"
                                    color="secondary"
                                    className="before-select"
                                    label={`${t('admin:adjustmentWeight')}*`}
                                    helperText={
                                        <span
                                            style={errors.adjustmentWeight ? { color: '#ae0015' } : {}}
                                            className="remaining-text"
                                        >
                                            {errors.adjustmentWeight
                                                ? errors.adjustmentWeight
                                                : t('admin:enterValueBetween', {
                                                      min: minAdjustmentWeight,
                                                      max: maxAdjustmentWeight,
                                                  })}
                                        </span>
                                    }
                                />
                            </FormControl>
                        </div>
                    </OutlinedWrapper>
                )}
            </DialogContent>
            <DialogActions className="edit-cm-dialog-actions dialog-actions flex-end">
                <SaveButton
                    id="edit-cm-save"
                    disabled={
                        disabledSaveButton.value ||
                        Object.keys(errors).filter((item) => (errors as any)[item]).length > 0 ||
                        !canEdit
                    }
                    text="update"
                    onClick={onUpdate}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditCmDialog;
